import React from 'react';
import { Link } from 'react-router-dom';
import { Ticket, Sparkles, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';

/**
 * Reusable "Create Event" CTA — surfaces the ticket-selling funnel
 * (Eventbrite-alternative) on non-organizer dashboards. Two visual
 * variants:
 *   - "button"  → compact pill, fits in a dashboard header row
 *   - "banner"  → full-width gradient card with copy + CTA, fits below
 *                 the welcome strip on quieter dashboards
 *
 * Routes to /sell-tickets (public landing) by default. When the caller
 * is already an organization, pass `target="/organization/dashboard"`
 * to short-circuit straight to their builder.
 */
export default function CreateEventButton({
  variant = 'button',
  target = '/sell-tickets',
  className = '',
  label = 'Create Event',
  testId = 'create-event-cta',
}) {
  if (variant === 'banner') {
    return (
      <Card
        className={`border-amber-500/30 bg-gradient-to-r from-amber-500/10 via-fuchsia-500/10 to-indigo-500/10 ${className}`}
        data-testid={`${testId}-banner`}
      >
        <CardContent className="p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
          <div className="space-y-1">
            <p className="text-[11px] uppercase tracking-[0.25em] font-bold text-amber-500 flex items-center gap-1.5">
              <Ticket className="h-3 w-3" /> Host your own events
            </p>
            <h3 className="font-bold text-base sm:text-lg">
              Sell tickets to your event on Eventism
            </h3>
            <p className="text-xs sm:text-sm text-muted-foreground">
              Lower fees than Eventbrite, marketing built in, $25 free wallet credit.
            </p>
          </div>
          <Link to={target} className="shrink-0">
            <Button
              className="h-10 px-5 bg-gradient-to-r from-amber-500 via-fuchsia-500 to-indigo-500 hover:opacity-90 text-white font-bold border-0"
              data-testid={testId}
            >
              <Sparkles className="h-3.5 w-3.5 mr-2" /> {label}
              <ArrowRight className="h-3.5 w-3.5 ml-2" />
            </Button>
          </Link>
        </CardContent>
      </Card>
    );
  }

  return (
    <Link to={target} className={className}>
      <Button
        className="h-10 px-4 bg-gradient-to-r from-amber-500 via-fuchsia-500 to-indigo-500 hover:opacity-90 text-white font-bold border-0 shadow-md shadow-fuchsia-500/20"
        data-testid={testId}
      >
        <Ticket className="h-4 w-4 mr-2" /> {label}
      </Button>
    </Link>
  );
}
